/**
 * Option Snapshot Service
 * Fetches option snapshot data from Massive via proxy client
 */

import { requestWithRetry, ProxyClientError, type ProxyRequestOptions } from "../../proxyClient";
import type { ProxyResponse } from "../../../shared/types";
import { buildOptionSnapshotEndpoint } from "./buildOptionSnapshotEndpoint";
import {
  mapMassiveOptionSnapshot,
  type OptionSnapshotResult,
  type ProxyOk,
  type MassiveOptionSnapshotResponse,
} from "../../../shared/options/optionSnapshot";
import { optionSnapshotCache, makeAsOfBucketKey } from "../../../shared/cache/sessionCache";
import { normalizeContractSymbol } from "../../../shared/symbols/occOptionSymbol";
import { getPublicConfig } from "../../../excel/config/getPublicConfig";
import { diagnosticsStore } from "../../../shared/diagnostics";
import { NotListedError } from "../../../shared/errors/notListedError";

/**
 * Extract request ID from response with standardized precedence
 * Note: requestWithRetry doesn't expose response headers, so we only check JSON fields
 * 1. Proxy wrapper request_id field
 * 2. Massive payload request_id field (if any)
 */
function extractRequestId(
  response: ProxyOk<MassiveOptionSnapshotResponse>
): string | undefined {
  // Precedence: wrapper > payload
  if (response.request_id) return response.request_id;
  if ((response.data as any)?.request_id) return (response.data as any).request_id;
  return undefined;
}

/**
 * Map ProxyClientError to OptionSnapshotError code
 * Uses primary mapping from error.code, with fallback from error.status
 */
function mapErrorCode(error: ProxyClientError): "auth" | "rate_limited" | "not_found" | "bad_request" | "backend" | "network" | "unknown" {
  // Primary mapping from error.code
  switch (error.code) {
    case "AUTH_REQUIRED":
    case "UPSTREAM_AUTH":
      return "auth";
    case "RATE_LIMIT_EXCEEDED":
    case "UPSTREAM_RATE_LIMITED":
      return "rate_limited";
    case "UPSTREAM_NOT_FOUND":
      return "not_found";
    case "VALIDATION_ERROR":
      return "bad_request";
    case "UPSTREAM_ERROR":
    case "BAD_PROXY_RESPONSE":
      return "backend";
    case "NETWORK_ERROR":
    case "TIMEOUT":
      return "network";
  }

  // Fallback mapping from error.status (HTTP status code)
  switch (error.status) {
    case 401:
    case 403:
      return "auth";
    case 404:
      return "not_found";
    case 429:
      return "rate_limited";
    case 400:
    case 422:
      return "bad_request";
    case 500:
    case 502:
    case 503:
    case 504:
      return "backend";
    case 0:
      return "network";
  }

  return "unknown";
}

/**
 * Clamp cache bucket size to safe bounds
 */
function clampCacheBucketMs(value: number | undefined): number {
  const DEFAULT = 5000;
  const MIN = 1000;
  const MAX = 60000;

  if (value === undefined || value === null || isNaN(value)) {
    return DEFAULT;
  }

  if (value < MIN) {
    return MIN;
  }

  if (value > MAX) {
    return MAX;
  }

  return value;
}

/**
 * Get option snapshot for a single contract
 * @param contractSymbol - OCC option symbol (e.g., "AAPL260130C00200000" or "O:AAPL260130C00200000")
 * @param opts - Optional: AbortSignal for cancellation, requestId for tracing, cache control, bucketMs override
 * @returns OptionSnapshotResult with snapshot data or error
 */
export async function getOptionSnapshot(
  contractSymbol: string,
  opts?: {
    signal?: AbortSignal;
    requestId?: string;
    cache?: "default" | "bypass" | "bypassSession";
    bucketMs?: number;
  }
): Promise<OptionSnapshotResult> {
  const cacheMode = opts?.cache || "default";
  const useCache = cacheMode === "default";
  const workerBypass = cacheMode === "bypass";
  const sessionBypass = cacheMode === "bypass" || cacheMode === "bypassSession";

  // Get bucketMs from config or override
  let bucketMs: number;
  if (opts?.bucketMs !== undefined) {
    bucketMs = clampCacheBucketMs(opts.bucketMs);
  } else {
    try {
      const config = await getPublicConfig();
      bucketMs = clampCacheBucketMs(config.public.cacheBucketMs);
      
      // Record diagnostics event if clamping occurred
      if (config.public.cacheBucketMs !== undefined && config.public.cacheBucketMs !== bucketMs) {
        diagnosticsStore.recordError({
          category: "backend",
          message: "cacheBucketMs clamped from " + config.public.cacheBucketMs + " to " + bucketMs,
          op_key: "OPTIONS.SNAPSHOT",
        });
      }
    } catch (error) {
      // Fallback to default if config fetch fails
      bucketMs = 5000;
    }
  }

  // Normalize contract symbol for cache key
  let canonicalSymbol: string;
  try {
    canonicalSymbol = normalizeContractSymbol(contractSymbol);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid OCC option symbol";
    return {
      ok: false,
      requestId: opts?.requestId,
      contractSymbol,
      error: {
        code: "bad_request",
        message: "Invalid OCC option symbol: " + message,
      },
    };
  }

  type SessionOutcome = "hit" | "miss" | "join" | "bypass";

  const fetchSnapshot = async (
    sessionOutcome: SessionOutcome,
    getJoinCount?: () => number
  ): Promise<OptionSnapshotResult> => {
    let endpoint;
    try {
      endpoint = buildOptionSnapshotEndpoint(contractSymbol);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invalid OCC option symbol";
      return {
        ok: false,
        requestId: opts?.requestId,
        contractSymbol,
        error: {
          code: "bad_request",
          message: "Invalid OCC option symbol: " + message,
        },
      };
    }

    // Snapshot GETs use simple headers only (no x-request-id, x-optlab-cache) to avoid CORS preflight.
    // request_id: use proxy response header x-request-id (already read in proxyClient).
    // Cache bypass: use query param so we don't send custom headers.
    const pathWithQuery =
      endpoint.path + (workerBypass ? (endpoint.path.includes("?") ? "&" : "?") + "cache=bypass" : "");

    const requestOptions: ProxyRequestOptions = {
      method: "GET",
      sessionCacheOutcome: sessionOutcome,
      ...(getJoinCount !== undefined && { sessionJoinCountGetter: getJoinCount }),
    };

    if (opts?.signal) {
      requestOptions.signal = opts.signal;
    }

    try {
      const response = await requestWithRetry<ProxyResponse>(
        pathWithQuery,
        requestOptions,
        "OPTIONS.SNAPSHOT"
      );

      // Type guard: ensure we have a successful response
      if (!response.ok) {
        // This shouldn't happen with requestWithRetry, but handle it defensively
        const errorResponse = response as any;
        return {
          ok: false,
          requestId: opts?.requestId || errorResponse.error?.request_id,
          contractSymbol,
          error: {
            code: "backend",
            message: errorResponse.error?.message || "Request failed",
          },
        };
      }

      // Cast to ProxyOk<MassiveOptionSnapshotResponse>
      const proxyResponse = response as ProxyOk<MassiveOptionSnapshotResponse>;

      // Extract request ID with standardized precedence
      // Note: requestWithRetry doesn't expose response headers, so request_id comes from JSON response
      const requestId = extractRequestId(proxyResponse);

      // Map to domain model
      return mapMassiveOptionSnapshot(proxyResponse, contractSymbol, requestId || opts?.requestId);
    } catch (error) {
      // Handle ProxyClientError: 404/not found → NotListedError so callers get #N/A (not #CONNECT)
      if (error instanceof ProxyClientError) {
        const errorCode = mapErrorCode(error);
        if (errorCode === "not_found") {
          throw new NotListedError(error.message || "Not listed");
        }
        return {
          ok: false,
          requestId: error.requestId || opts?.requestId,
          contractSymbol,
          error: {
            code: errorCode,
            message: error.message,
            details: error.details && typeof error.details === "object" && !Array.isArray(error.details)
              ? (error.details as Record<string, unknown>)
              : undefined,
          },
        };
      }

      // Handle unexpected errors
      const message = error instanceof Error ? error.message : "Unknown error";
      return {
        ok: false,
        requestId: opts?.requestId,
        contractSymbol,
        error: {
          code: "unknown",
          message: "Unexpected error: " + message,
          details: error instanceof Error ? { name: error.name, stack: error.stack } : undefined,
        },
      };
    }
  };

  if (useCache) {
    const cacheKey = makeAsOfBucketKey(canonicalSymbol, bucketMs);
    const ttlMs = bucketMs * 2;

    const cachedValue = optionSnapshotCache.get(cacheKey);
    if (cachedValue !== undefined) {
      diagnosticsStore.incrementCacheMetric("session_hit");
      diagnosticsStore.recordCacheEvent("session_hit", cacheKey.substring(0, 50));
      return cachedValue;
    }

    const result = await optionSnapshotCache.getOrSetWithLeaderStats<
      OptionSnapshotResult
    >(cacheKey, ttlMs, (getJoinCount) => fetchSnapshot("miss", getJoinCount), {
      onJoin: () => {
        diagnosticsStore.incrementCacheMetric("session_join");
        diagnosticsStore.recordCacheEvent("session_join", cacheKey.substring(0, 50));
      },
      shouldCache: (v) => Boolean(v && typeof v === "object" && "ok" in v && (v as { ok?: boolean }).ok === true),
    });

    if (result.outcome === "miss") {
      diagnosticsStore.incrementCacheMetric("session_miss");
      diagnosticsStore.recordCacheEvent("session_miss", cacheKey.substring(0, 50));
    }
    return result.value;
  }

  return fetchSnapshot("bypass");
}
